import { useState, useEffect } from "react";
import { Startgroup } from "./api/Dtos";
import { DELETE, PUT, POST } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import { HStack, VStack, Button, Spacer, Tooltip } from "@chakra-ui/react";
import { FastTable } from "./Table/FastTable";
import { useStartgroupsFromBackend } from "./Services/StartgroupService";
import AddStartgroupModal from "./Modals/AddStartgroupModal";
import { timeInMsToDateTimeString } from "./Services/ResultService";
import { start } from "repl";

const modifyFields = [
  "starttime_planned",
  "starttime_actual",
  "closetime_planned",
  "closetime_actual",
];

const DISPLAY_NAME_PLANNED_START = "Planerad start";
const DISPLAY_NAME_PLANNED_END = "Planerad klar";
const DISPLAY_NAME_ACTUAL_START = "Verklig start";
const DISPLAY_NAME_ACTUAL_END = "Verklig klar";

const modifyDisplayLabels = [
  DISPLAY_NAME_PLANNED_START,
  DISPLAY_NAME_ACTUAL_START,
  DISPLAY_NAME_PLANNED_END,
  DISPLAY_NAME_ACTUAL_END,
];

export function Startgroups() {
  const { startgroups, setStartgroups } = useStartgroupsFromBackend();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const globalContext = useGlobalContext();

  // Get competitions from backend
  useEffect(() => {
    console.log(
      "Startgroups component rendered with startgroups:",
      startgroups
    );
    setLoading(false);
    setError(
      globalContext.competition?.id !== undefined
        ? ""
        : "Ingen tävling är vald."
    );
  }, [startgroups]);

  if (loading) return <div>Loading...</div>;
  if (error.length > 0) return <div>Fel: {error}</div>;

  const addStartgroup = async (newStartgroup: Startgroup) => {
    console.info(
      "add: " +
        newStartgroup.starttime_planned +
        ", id: " +
        newStartgroup.id.toString()
    );
    if (globalContext.competition?.id !== undefined) {
      const success = await POST.startgroup(newStartgroup);
      if (success) {
        console.log("Added startgroup:", newStartgroup);
        setStartgroups((old) => [...old, newStartgroup]);
      }
    } else {
      console.error("Ingen tävling är vald.");
    }
  };

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        {
          <VStack>
            <HStack>
              <Tooltip label={"Skapa ny startgrupp"}>
                <Button
                  onClick={() => setIsModalOpen(true)}
                  colorScheme="green"
                  padding={"40px"}
                  fontSize={"24px"}
                >
                  Lägg till Startgrupp
                </Button>
              </Tooltip>
            </HStack>
            <AddStartgroupModal
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onAddRow={addStartgroup}
            />
          </VStack>
        }
      </HStack>

      <FastTable
        data={startgroups.map(mapStartgroupToDates)}
        pageHeader={`Startgrupper till ${globalContext.competition?.tag ?? "Ingen tävling vald"} ${new Date().toISOString().split('T')[0]}`}
        repeatHeaderRows={3}
        downloadFileName={`startgrupper_${globalContext.competition?.tag ?? "Ingen tävling vald"}_${new Date().toISOString().split('T')[0]}`}

        headers={[
          DISPLAY_NAME_PLANNED_START,
          DISPLAY_NAME_ACTUAL_START,
          DISPLAY_NAME_PLANNED_END,
          DISPLAY_NAME_ACTUAL_END,
        ]}
        forceDisplayToTypeMap={
          new Map([
            [DISPLAY_NAME_PLANNED_START, "Date"],
            [DISPLAY_NAME_ACTUAL_START, "Date"],
            [DISPLAY_NAME_PLANNED_END, "Date"],
            [DISPLAY_NAME_ACTUAL_END, "Date"],
          ])
        }
        rowFormat={"2fr 2fr 2fr 2fr 1fr 1fr"}
        toRowData={(p) => {
          const p2 = mapStartgroupWithDatesToStartgroup(p);
          return [
            timeInMsToDateTimeString(p2.starttime_planned),
            timeInMsToDateTimeString(p2.starttime_actual),
            timeInMsToDateTimeString(p2.closetime_planned),
            timeInMsToDateTimeString(p2.closetime_actual),
          ];
        }}
        modifiableFields={modifyFields as Array<keyof StartgroupWithDates>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        onChange={async (updatedStartgroup) => {
          if (
            globalContext.competition?.id !== undefined &&
            updatedStartgroup.starttime_planned != null &&
            updatedStartgroup.closetime_planned != null
          ) {
            const update =
              mapStartgroupWithDatesToStartgroup(updatedStartgroup);
            const success = await PUT.startgroup(
              update,
              globalContext.competition?.id
            );
            if (success) {
              setStartgroups((old) => {
                return old.map((p) =>
                  p.id === updatedStartgroup.id ? update : p
                );
              });
            }
            return success;
          } else {
            console.error("Ingen tävling är vald.");
          }
          return false;
        }}
        onDelete={async (deletedStartgroup) => {
          const startgroup =
            mapStartgroupWithDatesToStartgroup(deletedStartgroup);
          console.info(
            "Delete: " +
              startgroup.id +
              ", starttime_planned: " +
              startgroup.starttime_planned
          );
          const success = await DELETE.startgroup(startgroup);
          if (success) {
            setStartgroups((old) => {
              return old.filter((p) => p.id != deletedStartgroup.id);
            });
          }
          return true; // success;
        }}
      />
    </div>
  );
}

function msToDate(timeInMs: number | null | undefined) {
  if (timeInMs == null) {
    return undefined;
  }
  return new Date(timeInMs);
}

function mapStartgroupToDates(startgroup: Startgroup) {
  return {
    ...startgroup,
    closetime_actual: startgroup.closetime_actual
      ? new Date(startgroup.closetime_actual)
      : null,
    closetime_planned: startgroup.closetime_planned
      ? new Date(startgroup.closetime_planned)
      : null,
    starttime_actual: startgroup.starttime_actual
      ? new Date(startgroup.starttime_actual)
      : null,
    starttime_planned: startgroup.starttime_planned
      ? new Date(startgroup.starttime_planned)
      : null,
  } as StartgroupWithDates;
}

function mapStartgroupWithDatesToStartgroup(startgroup: StartgroupWithDates) {
  return {
    ...startgroup,
    closetime_actual: startgroup.closetime_actual
      ? startgroup.closetime_actual.getTime()
      : null,
    closetime_planned: startgroup.closetime_planned
      ? startgroup.closetime_planned.getTime()
      : null,
    starttime_actual: startgroup.starttime_actual
      ? startgroup.starttime_actual.getTime()
      : null,
    starttime_planned: startgroup.starttime_planned
      ? startgroup.starttime_planned.getTime()
      : null,
  } as Startgroup;
}

export interface StartgroupWithDates {
  id: number;
  starttime_planned: Date | null;
  starttime_actual: Date | null;
  closetime_planned: Date | null;
  closetime_actual: Date | null;
}

export default Startgroups;
